import { URLConstructor } from '../../../../../NonReact/utils/URLBuilder'
import { requestGetJSON } from '../../../../../NonReact/common/API/REST/RESTAPIClient'
import { INTERVAL_PERIOD } from '../Constants/Constants'
import { JSONResponse } from '../../../../../NonReact/common/Types/Types'

const MAX_RETRIES: number = 10
const POLLING_INTERVAL: number = 1000

/**
 * Function to open a popup window.
 *
 * @param location - URL for the popup window to go to.
 * @param name - Name of the window to open.
 * @param sizing - Sizing of the window to open.
 */
const openPopup = (location: URLConstructor, name: string, sizing: string): Nullable<Window> =>
    open(location.constructURL(), name, sizing)

/**
 * Function to check the status of the OAuth flow.
 *
 * @param statusLocation - URL for the status check.
 * @param statusSuccessFunc - Function to determine if the status check was successful.
 * @param errorOnMaxRetries - Error message to display if the status check fails.
 */
const onPopupClosed =
    <T extends unknown>(
        statusLocation: URLConstructor,
        statusSuccessFunc: (response: JSONResponse<T>) => boolean,
        errorOnMaxRetries: string
    ) =>
    (): Promise<Nullable<T>> => {
        const doStatusCheck = (counter: number): Promise<Nullable<T>> =>
            requestGetJSON<T>(statusLocation).then((status: JSONResponse<T>): Promise<Nullable<T>> => {
                if (statusSuccessFunc(status)) {
                    return Promise.resolve(status)
                }

                if (counter < MAX_RETRIES) {
                    return new Promise<Nullable<T>>((resolve, reject): void => {
                        setTimeout(() => {
                            doStatusCheck(counter + 1)
                                .then(resolve)
                                .catch(reject)
                        }, POLLING_INTERVAL)
                    })
                }

                return Promise.reject(new Error(errorOnMaxRetries))
            })

        return doStatusCheck(0)
    }

/**
 * Execute the OAuth flow.
 *
 * @param location - URL for the OAuth popup window to go to.
 * @param name - Name of the window to open.
 * @param sizing - Sizing of the window to open.
 * @param statusLocation - URL for the status check.
 * @param statusSuccessFunc - Function to determine if the status check was successful.
 * @param errorOnMaxRetries - Error message to display if the status check fails.
 */
export const executeOAuthFlow = <T extends unknown>(
    location: URLConstructor,
    name: string,
    sizing: string,
    statusLocation: URLConstructor,
    statusSuccessFunc: (response: JSONResponse<T>) => boolean,
    errorOnMaxRetries: string
): Promise<Nullable<T>> => {
    const popup = openPopup(location, name, sizing)

    if (popup) {
        return new Promise<Nullable<T>>((resolve, reject): void => {
            const intervalID: number = setInterval((): void => {
                if (popup.closed) {
                    clearInterval(intervalID)
                    onPopupClosed<T>(statusLocation, statusSuccessFunc, errorOnMaxRetries)().then(resolve).catch(reject)
                }
            }, INTERVAL_PERIOD) as unknown as number
        })
    }
    return Promise.reject(new Error(errorOnMaxRetries))
}
